import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../config";
import "../styles/BlockedContacts.css";

function BlockedContacts({ user, onBack, socket }) {
  const [blockedContacts, setBlockedContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblockingContactId, setUnblockingContactId] = useState(null);
  const [showUnblockConfirmation, setShowUnblockConfirmation] = useState(false);
  const [contactToUnblock, setContactToUnblock] = useState(null);

  useEffect(() => {
    fetchBlockedContacts();
  }, [user]);

  useEffect(() => {
    if (socket) {
      // Listen for real-time block/unblock events
      const handleUserBlocked = (data) => {
        if (data.blockerId === user.id) {
          // Refresh the blocked contacts list when current user blocks someone....
          fetchBlockedContacts();
        }
      };

      const handleUserUnblocked = (data) => {
        if (data.unblockerId === user.id) {
          // Remove the unblocked user from the list
          setBlockedContacts((prev) =>
            prev.filter((contact) => contact.id !== data.unblockedUserId)
          );
        }
      };

      socket.on("userBlocked", handleUserBlocked);
      socket.on("userUnblocked", handleUserUnblocked);

      return () => {
        socket.off("userBlocked", handleUserBlocked);
        socket.off("userUnblocked", handleUserUnblocked);
      };
    }
  }, [socket, user.id]);

  const fetchBlockedContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.apiUrl}/api/auth/users/${user.id}/blocked`
      );
      setBlockedContacts(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching blocked contacts:", error);
      setBlockedContacts([]);
      setLoading(false);
    }
  };

  const handleUnblockClick = (contact) => {
    setContactToUnblock(contact);
    setShowUnblockConfirmation(true);
  };

  const handleUnblockConfirm = async () => {
    if (!contactToUnblock) return;

    try {
      setUnblockingContactId(contactToUnblock.id);

      await axios.post(`${config.apiUrl}/api/auth/unblock-user`, {
        userId: user.id,
        targetUserId: contactToUnblock.id,
      });

      // Remove from local state
      setBlockedContacts((prev) =>
        prev.filter((contact) => contact.id !== contactToUnblock.id)
      );

      setShowUnblockConfirmation(false);
      setContactToUnblock(null);
    } catch (error) {
      console.error("Error unblocking contact:", error);
      alert(
        error.response?.data?.error ||
          "Failed to unblock contact. Please try again."
      );
    } finally {
      setUnblockingContactId(null);
    }
  };

  const handleUnblockCancel = () => {
    setShowUnblockConfirmation(false);
    setContactToUnblock(null);
  };

  const formatBlockedDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return "Today";
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="blocked-contacts">
      <div className="blocked-header">
        <button className="back-button" onClick={onBack}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Blocked contacts</h2>
      </div>

      <div className="blocked-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading blocked contacts...</p>
          </div>
        ) : blockedContacts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M4.93 4.93l14.14 14.14" />
              </svg>
            </div>
            <h3>No blocked contacts</h3>
            <p>
              When you block someone, they'll appear here. They won't be able to
              call you or send you messages.
            </p>
          </div>
        ) : (
          <>
            <div className="blocked-info">
              <p>Blocked contacts can't call you or send you messages.</p>
            </div>
            <div className="blocked-list">
              {blockedContacts.map((contact) => (
                <div key={contact.id} className="blocked-contact-item">
                  <div className="contact-avatar">
                    {contact.profileImage ? (
                      <img
                        src={`${config.apiUrl}${contact.profileImage}`}
                        alt={contact.username}
                        className="contact-profile-image"
                      />
                    ) : (
                      <div className="contact-avatar-placeholder notranslate">
                        {contact.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="contact-info">
                    <div className="contact-name notranslate">{contact.username}</div>
                    <div className="contact-blocked-date">
                      Blocked {formatBlockedDate(contact.blockedAt)}
                    </div>
                  </div>
                  <button
                    className="unblock-button"
                    onClick={() => handleUnblockClick(contact)}
                    disabled={unblockingContactId === contact.id}
                  >
                    {unblockingContactId === contact.id
                      ? "Unblocking..."
                      : "Unblock"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Unblock Confirmation Dialog */}
      {showUnblockConfirmation && contactToUnblock && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <h3>Unblock {contactToUnblock.username}?</h3>
            <p>
              {contactToUnblock.username} will be able to call you and send you
              messages again.
            </p>
            <div className="confirmation-buttons">
              <button
                className="cancel-button"
                onClick={handleUnblockCancel}
                disabled={unblockingContactId}
              >
                Cancel
              </button>
              <button
                className="confirm-button"
                onClick={handleUnblockConfirm}
                disabled={unblockingContactId}
              >
                {unblockingContactId ? "Unblocking..." : "Unblock"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlockedContacts;
